import { appState } from "./state";
import { notifyStateChange } from "./stateBus";
import { apiFetch } from "./api";
import { requestLock } from "./lockBus";
import type { ChatMessage } from "./types";

// Framework-agnostic REST operations for chat, split out of chat.ts so
// React can import them directly - chat.ts (now fully replaced by
// src/react/features/chat/*) is gone; unlike albums.ts/gallery.ts's earlier
// splits, there's no vanilla-only orchestration left needing to import
// dom.ts for chat, so this is the only chat data module now.

export async function loadConversationsData(): Promise<void> {
  try {
    const res = await apiFetch("/api/chat/conversations");
    if (res.status === 401) {
      requestLock("unauthorized");
      return;
    }
    if (!res.ok) return;
    const data = await res.json();
    appState.chatConversations = Array.isArray(data.conversations) ? data.conversations : [];
    notifyStateChange();
  } catch {
    // Best effort - the next WS event or manual reopen retries.
  }
}

export interface FetchMessagesOptions {
  // Mutually exclusive - callers pick exactly one (or none, for "the latest
  // page"). See server.js's ChatDB.getMessages() for what each does; this
  // is a thin, direct pass-through.
  before?: string;
  after?: string;
  around?: string;
  // The viewer's own local calendar day, "YYYY-MM-DD" (straight from an
  // <input type="date">, no conversion needed client-side) - the client's
  // timezone offset is sent alongside so the server can resolve it into
  // the right UTC boundaries; the server has no other way to know what
  // timezone the viewer is actually in.
  date?: string;
}

export interface FetchMessagesResult {
  messages: ChatMessage[];
  hasMoreOlder: boolean;
  hasMoreNewer: boolean;
  // Set only for `around`/`date` fetches - the message id the returned
  // window is centered on (for `date`, this is the server-resolved
  // anchor - the first message on/after that day, or the nearest one
  // before it if that day had none), so the caller can scroll/highlight
  // without a second round trip to learn which message "today" resolved to.
  anchorMessageId: string | null;
}

export async function fetchMessages(conversationId: string, opts: FetchMessagesOptions = {}): Promise<FetchMessagesResult | null> {
  const params = new URLSearchParams();
  if (opts.before) params.set("before", opts.before);
  if (opts.after) params.set("after", opts.after);
  if (opts.around) params.set("around", opts.around);
  if (opts.date) {
    params.set("date", opts.date);
    params.set("tzOffsetMinutes", String(new Date().getTimezoneOffset()));
  }
  const qs = params.toString();
  const res = await apiFetch(`/api/chat/conversations/${conversationId}/messages${qs ? `?${qs}` : ""}`);
  if (res.status === 401) {
    requestLock("unauthorized");
    return null;
  }
  if (!res.ok) throw new Error("Failed to load messages");
  const data = await res.json();
  return {
    messages: Array.isArray(data.messages) ? data.messages : [],
    hasMoreOlder: !!data.hasMoreOlder,
    hasMoreNewer: !!data.hasMoreNewer,
    anchorMessageId: data.anchorMessageId ?? null,
  };
}

// Fire-and-forget, no local appState write - purely tells the server to
// relay this to the other participant (see server.js's typing route
// comment for why this isn't rate-limited server-side; the caller
// (ChatThreadScreen.tsx) already debounces on its end).
export function sendTypingApi(conversationId: string, typing: boolean): void {
  apiFetch(`/api/chat/conversations/${conversationId}/typing`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ typing }),
  }).catch(() => {});
}

// Server-side text match (message content is encrypted at rest - see
// server.js's search route comment - there's nothing to search client-side).
// Empty query intentionally returns [] without a round trip.
export async function searchMessagesApi(conversationId: string, query: string): Promise<ChatMessage[]> {
  const q = query.trim();
  if (!q) return [];
  const res = await apiFetch(`/api/chat/conversations/${conversationId}/search?q=${encodeURIComponent(q)}`);
  if (res.status === 401) {
    requestLock("unauthorized");
    return [];
  }
  if (!res.ok) return [];
  const data = await res.json().catch(() => ({}));
  return Array.isArray(data.results) ? data.results : [];
}

// Every attachment-bearing message in this conversation, newest first -
// the "shared media" grid. Fetched on demand (not kept live in appState),
// same reasoning as fetchTogetherHistoryApi.
export async function fetchSharedMediaApi(conversationId: string): Promise<ChatMessage[]> {
  const res = await apiFetch(`/api/chat/conversations/${conversationId}/media`);
  if (res.status === 401) {
    requestLock("unauthorized");
    return [];
  }
  if (!res.ok) return [];
  const data = await res.json().catch(() => ({}));
  return Array.isArray(data.items) ? data.items : [];
}

// Orchestrates the mark-read REST call plus the local optimistic update -
// lives here (not inline in ChatThreadScreen) so ChatScreen.tsx's socket
// onNewMessage handler can call the exact same logic without needing to
// reach into ChatThreadScreen's internals (it may not even be the component
// that's currently mounted-with-content, since it early-returns null
// whenever no thread is open).
export async function markThreadMessagesRead(conversationId: string): Promise<void> {
  const hasUnread = appState.chatThreadMessages.some(
    (m) => m.senderEmail !== appState.currentUserEmail && !m.readAt && !m.deletedAt,
  );
  if (!hasUnread) return;

  try {
    const res = await apiFetch(`/api/chat/conversations/${conversationId}/read`, { method: "POST" });
    if (res.status === 401) {
      requestLock("unauthorized");
      return;
    }
    if (!res.ok) return;
    const now = new Date().toISOString();
    appState.chatThreadMessages = appState.chatThreadMessages.map((m) =>
      m.senderEmail !== appState.currentUserEmail && !m.readAt && !m.deletedAt ? { ...m, readAt: now } : m,
    );
    notifyStateChange();
    loadConversationsData();
  } catch {
    // Best effort.
  }
}

// All four write operations below share one result shape so callers can
// tell "already handled via requestLock, stay silent" (reason:
// "unauthorized") apart from "show the user an error toast" (reason:
// "error") - matching the original's `if (401) { requestLock; return; }
// if (!ok) { showToast(...); return; }` branching exactly.
export type ChatWriteResult<T> = { ok: true; data: T } | { ok: false; reason: "unauthorized" | "error"; error?: string };

export async function sendMessageApi(
  conversationId: string,
  text: string,
  replyToId?: string,
): Promise<ChatWriteResult<ChatMessage>> {
  const res = await apiFetch(`/api/chat/conversations/${conversationId}/messages`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(replyToId ? { text, replyToId } : { text }),
  });

  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false, reason: "unauthorized" };
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, reason: "error", error: data.message };
  }
  const data = await res.json();
  return { ok: true, data: data.message };
}

export async function sendAttachmentApi(
  conversationId: string,
  file: File,
  replyToId?: string,
): Promise<ChatWriteResult<ChatMessage>> {
  const fd = new FormData();
  fd.append("attachment", file);
  if (replyToId) fd.append("replyToId", replyToId);

  const res = await apiFetch(`/api/chat/conversations/${conversationId}/attachments`, {
    method: "POST",
    body: fd,
  });

  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false, reason: "unauthorized" };
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, reason: "error", error: data.message };
  }
  const data = await res.json();
  return { ok: true, data: data.message };
}

export async function sendVoiceApi(
  conversationId: string,
  file: File,
  durationSec: number,
  waveform: number[],
  replyToId?: string,
): Promise<ChatWriteResult<ChatMessage>> {
  const fd = new FormData();
  fd.append("voice", file);
  fd.append("durationSec", String(durationSec));
  fd.append("waveform", JSON.stringify(waveform));
  if (replyToId) fd.append("replyToId", replyToId);

  const res = await apiFetch(`/api/chat/conversations/${conversationId}/voice`, {
    method: "POST",
    body: fd,
  });

  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false, reason: "unauthorized" };
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, reason: "error", error: data.message };
  }
  const data = await res.json();
  return { ok: true, data: data.message };
}

// A single shape the optimistic-send machinery in ChatThreadScreen.tsx
// funnels text/attachment/voice sends through - see sendOptimistic() there.
export interface PendingSendPayload {
  kind: "text" | "attachment" | "voice";
  text?: string;
  file?: File;
  durationSec?: number;
  waveform?: number[];
  replyToId?: string;
}

// Sender-only, within MESSAGE_EDIT_WINDOW_MS of createdAt - see server.js's
// ChatDB.editMessage comment. A rejected edit (window expired, or some
// other error) surfaces data.message via the `error` field, same as
// sendMessageApi, since "your window to edit this ran out" is worth
// telling the user specifically rather than a generic failure toast.
export async function editMessageApi(conversationId: string, messageId: string, text: string): Promise<ChatWriteResult<ChatMessage>> {
  const res = await apiFetch(`/api/chat/conversations/${conversationId}/messages/${messageId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text }),
  });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false, reason: "unauthorized" };
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, reason: "error", error: data.message };
  }
  const data = await res.json();
  return { ok: true, data: data.message };
}

export async function deleteMessageApi(conversationId: string, messageId: string): Promise<ChatWriteResult<true>> {
  const res = await apiFetch(`/api/chat/conversations/${conversationId}/messages/${messageId}`, {
    method: "DELETE",
  });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false, reason: "unauthorized" };
  }
  if (!res.ok) return { ok: false, reason: "error" };
  return { ok: true, data: true };
}

export async function reactToMessageApi(
  conversationId: string,
  messageId: string,
  emoji: string | null,
): Promise<ChatWriteResult<ChatMessage["reactions"]>> {
  const res = await apiFetch(`/api/chat/conversations/${conversationId}/messages/${messageId}/react`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ emoji }),
  });

  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false, reason: "unauthorized" };
  }
  if (!res.ok) return { ok: false, reason: "error" };
  const data = await res.json();
  return { ok: true, data: data.reactions };
}
